import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/pt-br';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { useClickOutside } from '@shared/hooks';
import { cn } from '@shared/utils';

import { DateCalendarPopup } from './DateCalendarPopup';

dayjs.extend(customParseFormat);
dayjs.locale('pt-br');

export interface DateInputProps {
  readonly id?: string;
  readonly label?: string;
  readonly value?: string;
  readonly onChange?: (e: { target: { value: string } }) => void;
  readonly invalid?: boolean;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
}

export interface UseDateTextInputResult {
  readonly inputText: string;
  readonly viewDate: dayjs.Dayjs;
  readonly selectedDate: dayjs.Dayjs | null;
  readonly days: (dayjs.Dayjs | null)[];
  readonly handleInputChange: (text: string) => void;
  readonly selectDate: (date: dayjs.Dayjs) => void;
  readonly prevMonth: () => void;
  readonly nextMonth: () => void;
}

/**
 * Encapsulates the text-typing/calendar-navigation logic shared between `DateInput` and
 * `CompactDateInput`: digit formatting into `DD/MM/YY`, parsing into an ISO `value`, and
 * the calendar view state (month navigation, day grid).
 */
export function useDateTextInput(
  value: string | undefined,
  onChange: ((e: { target: { value: string } }) => void) | undefined,
): UseDateTextInputResult {
  const [viewDate, setViewDate] = useState(() => {
    if (value) return dayjs(value);
    return dayjs();
  });
  const [inputText, setInputText] = useState(() => {
    if (value) return dayjs(value).format('DD/MM/YY');
    return '';
  });

  useEffect(() => {
    if (value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing input display with external value change is intentional
      setInputText(dayjs(value).format('DD/MM/YY'));
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing calendar view with external value change is intentional
      setViewDate(dayjs(value));
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing input on external reset is intentional
      setInputText('');
    }
  }, [value]);

  const handleInputChange = useCallback(
    (text: string) => {
      const digits = text.replace(/\D/g, '');
      let formatted = '';
      if (digits.length > 0) formatted = digits.slice(0, 2);
      if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
      if (digits.length > 4) formatted += '/' + digits.slice(4, 6);
      setInputText(formatted);

      if (digits.length === 6) {
        const parsed = dayjs(formatted, 'DD/MM/YY');
        if (parsed.isValid()) {
          setViewDate(parsed);
          setInputText(parsed.format('DD/MM/YY'));
          onChange?.({ target: { value: parsed.format('YYYY-MM-DD') } });
        }
      } else if (digits.length === 0) {
        onChange?.({ target: { value: '' } });
      }
    },
    [onChange],
  );

  const selectDate = useCallback(
    (date: dayjs.Dayjs) => {
      setInputText(date.format('DD/MM/YY'));
      setViewDate(date);
      onChange?.({ target: { value: date.format('YYYY-MM-DD') } });
    },
    [onChange],
  );

  const prevMonth = useCallback(() => setViewDate((prev) => prev.subtract(1, 'month')), []);
  const nextMonth = useCallback(() => setViewDate((prev) => prev.add(1, 'month')), []);

  const selectedDate = value ? dayjs(value) : null;

  const daysInMonth = viewDate.daysInMonth();
  const firstDayOfWeek = viewDate.startOf('month').day();

  const days: (dayjs.Dayjs | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(viewDate.date(d));
  }

  return {
    inputText,
    viewDate,
    selectedDate,
    days,
    handleInputChange,
    selectDate,
    prevMonth,
    nextMonth,
  };
}

export function DateInput({
  id,
  label,
  value,
  onChange,
  invalid,
  error,
  disabled,
  placeholder = 'dd/mm/aa',
  className,
}: DateInputProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    inputText,
    viewDate,
    selectedDate,
    days,
    handleInputChange,
    selectDate,
    prevMonth,
    nextMonth,
  } = useDateTextInput(value, onChange);

  useClickOutside([{ ref: containerRef, close: () => setIsOpen(false) }]);

  const handleSelectDate = useCallback(
    (date: dayjs.Dayjs) => {
      selectDate(date);
      setIsOpen(false);
    },
    [selectDate],
  );

  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-1.5', className)}>
      {label !== undefined ? (
        <label htmlFor={id} className="text-xs font-semibold text-login-text-secondary">
          {label}
        </label>
      ) : null}

      <div className="relative">
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.625 10.625C10.625 10.9702 10.3452 11.25 10 11.25C9.65482 11.25 9.375 10.9702 9.375 10.625C9.375 10.2798 9.65482 10 10 10C10.3452 10 10.625 10.2798 10.625 10.625Z"
              fill="#A4A7AE"
            />
            <path
              d="M6.25 13.125C6.59518 13.125 6.875 12.8452 6.875 12.5C6.875 12.1548 6.59518 11.875 6.25 11.875C5.90482 11.875 5.625 12.1548 5.625 12.5C5.625 12.8452 5.90482 13.125 6.25 13.125Z"
              fill="#A4A7AE"
            />
            <path
              d="M6.875 14.375C6.875 14.7202 6.59518 15 6.25 15C5.90482 15 5.625 14.7202 5.625 14.375C5.625 14.0298 5.90482 13.75 6.25 13.75C6.59518 13.75 6.875 14.0298 6.875 14.375Z"
              fill="#A4A7AE"
            />
            <path
              d="M8.125 13.125C8.47018 13.125 8.75 12.8452 8.75 12.5C8.75 12.1548 8.47018 11.875 8.125 11.875C7.77982 11.875 7.5 12.1548 7.5 12.5C7.5 12.8452 7.77982 13.125 8.125 13.125Z"
              fill="#A4A7AE"
            />
            <path
              d="M8.75 14.375C8.75 14.7202 8.47018 15 8.125 15C7.77982 15 7.5 14.7202 7.5 14.375C7.5 14.0298 7.77982 13.75 8.125 13.75C8.47018 13.75 8.75 14.0298 8.75 14.375Z"
              fill="#A4A7AE"
            />
            <path
              d="M10 13.125C10.3452 13.125 10.625 12.8452 10.625 12.5C10.625 12.1548 10.3452 11.875 10 11.875C9.65482 11.875 9.375 12.1548 9.375 12.5C9.375 12.8452 9.65482 13.125 10 13.125Z"
              fill="#A4A7AE"
            />
            <path
              d="M10.625 14.375C10.625 14.7202 10.3452 15 10 15C9.65482 15 9.375 14.7202 9.375 14.375C9.375 14.0298 9.65482 13.75 10 13.75C10.3452 13.75 10.625 14.0298 10.625 14.375Z"
              fill="#A4A7AE"
            />
            <path
              d="M11.875 13.125C12.2202 13.125 12.5 12.8452 12.5 12.5C12.5 12.1548 12.2202 11.875 11.875 11.875C11.5298 11.875 11.25 12.1548 11.25 12.5C11.25 12.8452 11.5298 13.125 11.875 13.125Z"
              fill="#A4A7AE"
            />
            <path
              d="M12.5 14.375C12.5 14.7202 12.2202 15 11.875 15C11.5298 15 11.25 14.7202 11.25 14.375C11.25 14.0298 11.5298 13.75 11.875 13.75C12.2202 13.75 12.5 14.0298 12.5 14.375Z"
              fill="#A4A7AE"
            />
            <path
              d="M13.75 13.125C14.0952 13.125 14.375 12.8452 14.375 12.5C14.375 12.1548 14.0952 11.875 13.75 11.875C13.4048 11.875 13.125 12.1548 13.125 12.5C13.125 12.8452 13.4048 13.125 13.75 13.125Z"
              fill="#A4A7AE"
            />
            <path
              d="M12.5 10.625C12.5 10.9702 12.2202 11.25 11.875 11.25C11.5298 11.25 11.25 10.9702 11.25 10.625C11.25 10.2798 11.5298 10 11.875 10C12.2202 10 12.5 10.2798 12.5 10.625Z"
              fill="#A4A7AE"
            />
            <path
              d="M13.75 11.25C14.0952 11.25 14.375 10.9702 14.375 10.625C14.375 10.2798 14.0952 10 13.75 10C13.4048 10 13.125 10.2798 13.125 10.625C13.125 10.9702 13.4048 11.25 13.75 11.25Z"
              fill="#A4A7AE"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.625 1.875C5.97018 1.875 6.25 2.15482 6.25 2.5V3.75H13.75V2.5C13.75 2.15482 14.0298 1.875 14.375 1.875C14.7202 1.875 15 2.15482 15 2.5V3.75H15.625C17.0057 3.75 18.125 4.86929 18.125 6.25V15.625C18.125 17.0057 17.0057 18.125 15.625 18.125H4.375C2.99429 18.125 1.875 17.0057 1.875 15.625V6.25C1.875 4.86929 2.99429 3.75 4.375 3.75H5V2.5C5 2.15482 5.27982 1.875 5.625 1.875ZM16.875 9.375C16.875 8.68464 16.3154 8.125 15.625 8.125H4.375C3.68464 8.125 3.125 8.68464 3.125 9.375V15.625C3.125 16.3154 3.68464 16.875 4.375 16.875H15.625C16.3154 16.875 16.875 16.3154 16.875 15.625V9.375Z"
              fill="#A4A7AE"
            />
          </svg>
        </span>
        <input
          id={id}
          type="text"
          value={inputText}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={8}
          aria-invalid={invalid === true ? 'true' : undefined}
          className={cn(
            'flex h-10 w-full rounded-md border bg-white pl-8 pr-3 py-2',
            'text-sm text-login-text-secondary',
            'placeholder:text-[#A4A7AE]',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-60',
            'transition-colors',
            invalid === true
              ? 'border-login-border-error focus-visible:ring-login-border-error'
              : 'border-login-border-primary hover:border-login-border-brand',
          )}
        />
      </div>

      {error !== undefined ? <span className="text-xs text-login-error-text">{error}</span> : null}

      {isOpen ? (
        <DateCalendarPopup
          viewDate={viewDate}
          selectedDate={selectedDate}
          days={days}
          onSelectDate={handleSelectDate}
          onPrevMonth={prevMonth}
          onNextMonth={nextMonth}
          className="top-full right-0 w-64"
        />
      ) : null}
    </div>
  );
}
